/**
 * MyQuestionnairesPage — "Mis cuestionarios"
 *
 * FINAL-DEMO-PROFILES-QUESTIONNAIRES-RL-OPS-FIX-1 (Feb 2026):
 * Reuses the SAME `/questionnaires` source the Panel uses for its
 * "Tus cuestionarios" block. If the user sees a questionnaire on the
 * Panel, it MUST appear here too. No new backend, no DB writes, no
 * new endpoints. Empty state only renders when the source returns 0
 * items for this profile.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/i18n";
import PanelLayout from "@/components/layout/PanelLayout";
import { ListChecks, Clock, ShieldCheck, CheckCircle2, ChevronRight } from "lucide-react";

const COPY = {
  es: {
    eyebrow: "Sección",
    badge: "Preview controlado",
    title: "Mis cuestionarios",
    intro:
      "Cuestionarios asociados a tu perfil. Aquí verás pendientes, en borrador y completados, y podrás retomarlos o consultar tus respuestas.",
    emptyTitle: "Todavía no tienes cuestionarios asociados a tu perfil",
    emptyBody:
      "Cuando tengas cuestionarios pendientes o completados, aparecerán aquí.",
    completed: "Completado",
    open: "Abrir",
    locked: "Verifica tu email primero",
    questionsCount: "preguntas",
    sectionPending: "Pendientes",
    sectionDone: "Completados",
  },
  en: {
    eyebrow: "Section",
    badge: "Controlled preview",
    title: "My questionnaires",
    intro:
      "Questionnaires linked to your profile. You'll see pending, drafts and completed entries, and you can resume them or review your answers.",
    emptyTitle: "No questionnaires linked to your profile yet",
    emptyBody:
      "Once you have pending or completed questionnaires, they'll appear here.",
    completed: "Completed",
    open: "Open",
    locked: "Verify your email first",
    questionsCount: "questions",
    sectionPending: "Pending",
    sectionDone: "Completed",
  },
};

export default function MyQuestionnairesPage() {
  const { user } = useAuth();
  const { lang, pickI18n, tQuestionnaireTitle } = useLang();
  const copy = COPY[lang] || COPY.en;
  const [questionnaires, setQuestionnaires] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/questionnaires")
      .then((r) => setQuestionnaires(Array.isArray(r.data) ? r.data : []))
      .catch(() => setQuestionnaires([]))
      .finally(() => setLoading(false));
  }, []);

  const pending = questionnaires.filter((q) => !q.completed);
  const done = questionnaires.filter((q) => q.completed);
  const verifyGated = !user?.email_verified;

  const renderCard = (q) => {
    const cardInner = (
      <>
        <div className="flex items-start justify-between">
          <div>
            <div className="font-display font-semibold text-slate-900 text-base leading-snug">
              {tQuestionnaireTitle(q)}
            </div>
            <p className="text-sm text-slate-500 mt-1 line-clamp-2">
              {pickI18n(q, "description") || q.description_en}
            </p>
          </div>
          {q.completed && (
            <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
          )}
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-500">
            {q.questions?.length || 0} {copy.questionsCount}
          </span>
          {verifyGated && !q.completed ? (
            <span
              className="text-amber-700 font-medium inline-flex items-center gap-1"
              data-testid={`my-questionnaire-locked-${q.id}`}
            >
              {copy.locked}
            </span>
          ) : (
            <span className="text-emerald-700 font-medium inline-flex items-center gap-1">
              {q.completed ? copy.completed : copy.open}
              <ChevronRight className="w-4 h-4 rtl:rotate-180" />
            </span>
          )}
        </div>
      </>
    );

    if (verifyGated && !q.completed) {
      return (
        <div
          key={q.id}
          className="tsw-card p-5 flex flex-col gap-3 opacity-60 cursor-not-allowed select-none"
          aria-disabled="true"
          data-testid={`my-questionnaire-card-${q.id}`}
        >
          {cardInner}
        </div>
      );
    }
    return (
      <Link
        to={`/questionnaire/${q.id}`}
        key={q.id}
        className="tsw-card tsw-card-hover p-5 flex flex-col gap-3"
        data-testid={`my-questionnaire-card-${q.id}`}
      >
        {cardInner}
      </Link>
    );
  };

  return (
    <PanelLayout>
      <div data-testid="page-my-questionnaires">
        <header className="flex flex-wrap items-center gap-3 mb-2">
          <ListChecks className="w-6 h-6 text-emerald-700" />
          <div className="tsw-overline">{copy.eyebrow}</div>
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-semibold uppercase tracking-wider"
            data-testid="page-my-questionnaires-badge"
          >
            <ShieldCheck className="w-3 h-3" />
            {copy.badge}
          </span>
        </header>

        <h1
          className="text-2xl font-bold text-slate-900"
          data-testid="page-my-questionnaires-title"
        >
          {copy.title}
        </h1>
        <p className="text-sm text-slate-600 mt-1 max-w-2xl">{copy.intro}</p>

        {loading ? (
          <div
            className="mt-5 rounded-xl border border-slate-200 bg-white p-6 text-center"
            data-testid="page-my-questionnaires-loading"
          >
            <Clock className="w-6 h-6 text-slate-400 mx-auto mb-2 animate-pulse" />
            <p className="text-sm text-slate-500">…</p>
          </div>
        ) : questionnaires.length === 0 ? (
          <div
            className="mt-5 rounded-xl border border-slate-200 bg-white p-6"
            data-testid="page-my-questionnaires-empty"
          >
            <div className="text-center">
              <Clock className="w-6 h-6 text-slate-400 mx-auto mb-2" />
              <h3 className="font-semibold text-slate-800">{copy.emptyTitle}</h3>
              <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
                {copy.emptyBody}
              </p>
            </div>
          </div>
        ) : (
          <div className="mt-6 space-y-8">
            {pending.length > 0 && (
              <section data-testid="page-my-questionnaires-pending">
                <h2 className="font-display font-semibold text-lg text-slate-900 mb-3">
                  {copy.sectionPending} · {pending.length}
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {pending.map(renderCard)}
                </div>
              </section>
            )}
            {done.length > 0 && (
              <section data-testid="page-my-questionnaires-done">
                <h2 className="font-display font-semibold text-lg text-slate-900 mb-3">
                  {copy.sectionDone} · {done.length}
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {done.map(renderCard)}
                </div>
              </section>
            )}
          </div>
        )}
      </div>
    </PanelLayout>
  );
}
